import React, { useRef, useEffect, forwardRef } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';

import {
    autoExpandTextarea,
    resetTextareaHeight,
} from '@/shared/utils/chatHelpers';
import plus from '@/assets/img/icons/svg/plus.svg';
import mic from '@/assets/img/icons/svg/mic.svg';
import ear from '@/assets/img/icons/svg/ear.svg';

/**
 * Shared chat input pill used by BiliChat (full page) and ChatWidget (floating).
 *
 * Handles auto-expanding textarea, Enter-to-send, and height reset after send.
 * Pass showExtraActions to render the attachment / voice / mic buttons (BiliChat only).
 */
const ChatInput = forwardRef(function ChatInput(
    {
        value,
        onValueChange,
        onSend,
        disabled = false,
        placeholder = 'Chat with Bili',
        showExtraActions = false,
        minHeight = 36,
        maxHeight = 150,
    },
    ref
) {
    const internalRef = useRef(null);

    // Keep both the forwarded ref and our internal ref in sync
    const setRef = (el) => {
        internalRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
    };

    const getMinPx = () =>
        typeof minHeight === 'function' ? minHeight() : minHeight;
    const getMaxPx = () =>
        typeof maxHeight === 'function' ? maxHeight() : maxHeight;

    // Reset textarea height when value is cleared (e.g. after send)
    useEffect(() => {
        if (!value) {
            const minPx =
                typeof minHeight === 'function' ? minHeight() : minHeight;
            resetTextareaHeight(internalRef, minPx);
        }
    }, [value, minHeight]);

    const handleChange = (e) => {
        autoExpandTextarea(internalRef, e.target.value, getMinPx(), getMaxPx());
        onValueChange(e.target.value);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSend();
        }
    };

    return (
        <div className="chat-input-pill">
            <Form.Control
                ref={setRef}
                as="textarea"
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="chat-pill-textarea"
                disabled={disabled}
                aria-label="Chat message input"
            />
            <div
                className={`d-flex align-items-center mt-2 ${showExtraActions ? 'justify-content-between' : 'justify-content-end'}`}
            >
                {showExtraActions && (
                    <div className="d-flex align-items-center gap-2">
                        <button
                            type="button"
                            className="chat-pill-btn"
                            aria-label="Add attachment"
                        >
                            <img src={plus} alt="" className="chat-icon" />
                        </button>
                        <button
                            type="button"
                            className="chat-pill-btn d-none d-sm-flex"
                            aria-label="Voice input"
                        >
                            <img src={ear} alt="" className="chat-icon" />
                        </button>
                    </div>
                )}
                <div className="d-flex align-items-center gap-2">
                    {showExtraActions && (
                        <button
                            type="button"
                            className="chat-pill-btn d-none d-sm-flex"
                            aria-label="Microphone"
                        >
                            <img src={mic} alt="" className="chat-icon" />
                        </button>
                    )}
                    <button
                        type="button"
                        className="chat-pill-send-btn"
                        onClick={onSend}
                        disabled={disabled || !value.trim()}
                        aria-label="Send message"
                    >
                        {disabled ? (
                            <Spinner
                                animation="border"
                                size="sm"
                                className="text-primary"
                            />
                        ) : (
                            <i
                                className={`bi ${value.trim() ? 'bi-arrow-up-circle-fill' : 'bi-arrow-up-circle'} chat-send-icon`}
                                aria-hidden="true"
                            />
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
});

ChatInput.displayName = 'ChatInput';

ChatInput.propTypes = {
    value: PropTypes.string.isRequired,
    onValueChange: PropTypes.func.isRequired,
    onSend: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    showExtraActions: PropTypes.bool,
    minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
    maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
};

export default ChatInput;
